import type { StcCongesConfig } from '@application/rh/StcConfig';
import type { Conge } from '@shared/types/Conge';
import type { Salarie } from '@shared/types/Salarie';

/** Loi 3/94 : 24 jours ouvrables par an (2 jours par mois). */
export const CONGES_JOURS_ANNUELS_LOI = 24;

export const DEFAULT_CONGES_CONFIG: StcCongesConfig = {
  jours_par_an: CONGES_JOURS_ANNUELS_LOI,
  base_jours: 30,
  deduire_conges_pris: true,
  report_multi_annees: true,
  report_plafond_jours: 0,
};

function dateRefIso(salarie: Salarie, dateRef?: Date): string {
  if (salarie.date_sortie) return salarie.date_sortie;
  return (dateRef ?? new Date()).toISOString().slice(0, 10);
}

function moisTotauxTravailles(salarie: Salarie, dateRef?: Date): number {
  const debut = new Date(`${salarie.date_embauche}T00:00:00.000Z`);
  const fin = new Date(`${dateRefIso(salarie, dateRef)}T00:00:00.000Z`);
  if (Number.isNaN(debut.getTime()) || Number.isNaN(fin.getTime()) || fin < debut) return 0;
  return (
    (fin.getUTCFullYear() - debut.getUTCFullYear()) * 12 +
    (fin.getUTCMonth() - debut.getUTCMonth())
  );
}

function moisTravaillesAnnee(salarie: Salarie, dateRef?: Date): number {
  const ref = dateRefIso(salarie, dateRef);
  const annee = Number(ref.slice(0, 4));
  const debutAnnee = `${annee}-01-01`;
  const debut = salarie.date_embauche > debutAnnee ? salarie.date_embauche : debutAnnee;
  const debutD = new Date(`${debut}T00:00:00.000Z`);
  const finD = new Date(`${ref}T00:00:00.000Z`);
  if (Number.isNaN(debutD.getTime()) || Number.isNaN(finD.getTime()) || finD < debutD) return 0;
  return (
    (finD.getUTCFullYear() - debutD.getUTCFullYear()) * 12 +
    (finD.getUTCMonth() - debutD.getUTCMonth())
  );
}

function congesPris(salarie: Salarie, conges: readonly Conge[], annee?: string): number {
  return conges
    .filter((c) => {
      if (c.salarie_id !== salarie.id || c.type !== 'annuel') return false;
      if (c.statut !== 'valide' && c.statut !== 'pris') return false;
      if (annee && !c.date_debut.startsWith(annee)) return false;
      return true;
    })
    .reduce((s, c) => s + (c.jours || 0), 0);
}

export function configCongesDepuisStc(conges?: StcCongesConfig): StcCongesConfig {
  return {
    ...DEFAULT_CONGES_CONFIG,
    ...conges,
    jours_par_an: conges?.jours_par_an && conges.jours_par_an > 0 ? conges.jours_par_an : CONGES_JOURS_ANNUELS_LOI,
  };
}

/**
 * Solde unique (écran RH, espace salarié, STC, attestation).
 * `conges_solde` saisi sur la fiche reste prioritaire (solde figé pour sortie).
 */
export function calculerSoldeCongesUnifie(
  salarie: Salarie,
  conges: readonly Conge[] = [],
  config: StcCongesConfig = DEFAULT_CONGES_CONFIG,
  dateRef?: Date,
): { acquis: number; pris: number; solde: number; fige: boolean } {
  if (typeof salarie.conges_solde === 'number' && !Number.isNaN(salarie.conges_solde)) {
    return { acquis: salarie.conges_solde, pris: 0, solde: salarie.conges_solde, fige: true };
  }
  const cc = configCongesDepuisStc(config);
  const joursAn = cc.jours_par_an;
  const multi = cc.report_multi_annees !== false;
  const acquis = multi
    ? joursAn * (moisTotauxTravailles(salarie, dateRef) / 12)
    : joursAn * (moisTravaillesAnnee(salarie, dateRef) / 12);
  let pris = 0;
  if (cc.deduire_conges_pris !== false) {
    pris = multi
      ? congesPris(salarie, conges)
      : congesPris(salarie, conges, dateRefIso(salarie, dateRef).slice(0, 4));
  }
  let solde = acquis - pris;
  if (cc.report_plafond_jours && cc.report_plafond_jours > 0) {
    solde = Math.min(solde, cc.report_plafond_jours);
  }
  return {
    acquis: Math.max(0, Math.round(acquis * 10) / 10),
    pris: Math.max(0, Math.round(pris * 10) / 10),
    solde: Math.max(0, Math.round(solde * 10) / 10),
    fige: false,
  };
}
